import clsx from "clsx";
import { CircleCheck, OctagonAlert, TriangleAlert } from "lucide-react";

import type { CurvePhase, DtoBand, LogisticsMaintenance, MaintenanceColor, MaintenanceFamily, MaterielFamily } from "../../api/types";
import { Badge } from "../../components/ui";
import { formatNumber, k, t } from "../../i18n/i18n";
import { bandText, colorTone, CURVE_PHASES, dtoColor, maintenanceColor, percentText } from "../../maintenance";

/** Pièces de la vue Maintenance (chantier « DTO / CAPOPS », révision « DTO réaliste ») : icônes d'état, l'une pour la
 *  DTO (jugée contre la DTO attendue de son temps), l'autre pour la CAPOPS (seuils de l'iPad), jamais confondues ;
 *  courbe de T0 à T3 sur le fond de la trajectoire attendue ; pannes, dommages et réparations de chaque temps. La
 *  couleur va toujours avec une icône de forme différente et un texte : jamais la couleur seule. */

const num = (value: number, digits = 1) => formatNumber(value, { minimumFractionDigits: digits, maximumFractionDigits: digits });

const ICONS: Record<MaintenanceColor, typeof CircleCheck> = { green: CircleCheck, orange: TriangleAlert, red: OctagonAlert };

/** Lecture d'une DTO, au survol et pour les lecteurs d'écran. */
const DTO_STATUS: Record<MaintenanceColor, string> = {
  green: k("Conforme à la DTO attendue"),
  orange: k("Sous la DTO attendue de moins de 5 points"),
  red: k("Sous la DTO attendue de 5 points ou plus"),
};

/** Lecture d'une CAPOPS (seuils de l'iPad). */
const CAPOPS_STATUS: Record<MaintenanceColor, string> = {
  green: k("Au-dessus de 80 %"),
  orange: k("De 61 à 80 %"),
  red: k("60 % ou moins"),
};

export const TONE_TEXT = { ok: "text-ok", warn: "text-warn", danger: "text-danger", neutral: "text-ink-muted" } as const;

function StateIcon({ color, text, size }: { color: MaintenanceColor; text: string; size: number }) {
  const Icon = ICONS[color];
  return (
    <span role="img" aria-label={t(text)} title={t(text)} className={clsx("flex shrink-0", TONE_TEXT[colorTone(color)])}>
      <Icon size={size} aria-hidden />
    </span>
  );
}

/** Icône d'état d'une DTO face à la borne basse `low` de la DTO attendue de son temps. */
export function DtoStatusIcon({ percent, low, size = 14 }: { percent: number; low: number; size?: number }) {
  const color = dtoColor(percent, low) ?? "red";
  return <StateIcon color={color} text={DTO_STATUS[color]} size={size} />;
}

/** Icône d'état d'une CAPOPS (vert > 80 %, orange 61 à 80 %, rouge ≤ 60 %). */
export function CapopsStatusIcon({ percent, size = 14 }: { percent: number; size?: number }) {
  const color = maintenanceColor(percent) ?? "red";
  return <StateIcon color={color} text={CAPOPS_STATUS[color]} size={size} />;
}

/** DTO en badge coloré face à l'attendu, précédée de son icône d'état. */
export function DtoBadge({ percent, low }: { percent: number; low: number }) {
  const color = dtoColor(percent, low) ?? "red";
  const Icon = ICONS[color];
  return (
    <Badge tone={colorTone(color)} title={t(DTO_STATUS[color])}>
      <Icon size={12} aria-hidden className="mr-1" />
      {percentText(percent)}
    </Badge>
  );
}

/** CAPOPS en badge coloré (seuils de l'iPad), précédée de son icône d'état. */
export function CapopsBadge({ percent }: { percent: number }) {
  const color = maintenanceColor(percent) ?? "red";
  const Icon = ICONS[color];
  return (
    <Badge tone={colorTone(color)} title={t(CAPOPS_STATUS[color])}>
      <Icon size={12} aria-hidden className="mr-1" />
      {percentText(percent)}
    </Badge>
  );
}

/** Courbe de T0 à T3 d'une famille, sobre : bande grise de la DTO attendue (trajectoire, bornes réglables), nommée
 *  par la légende sous le dessin, trait de la DTO, points colorés face à l'attendu avec leur valeur, temps affiché en
 *  gras ; ni grille ni axe gradué (les valeurs sont écrites). Échelle de 40 points au moins, de la dizaine autour des
 *  valeurs et des bandes. */
export function DtoCurve({
  family,
  expected,
  phase,
}: {
  family: MaintenanceFamily;
  expected: Record<CurvePhase, DtoBand>;
  phase: CurvePhase;
}) {
  const W = 320;
  const H = 112;
  const left = 22;
  const right = 22;
  const top = 18;
  const bottom = 22;
  const values = CURVE_PHASES.map((p) => family.dto[p]);
  const lows = CURVE_PHASES.map((p) => expected[p].low);
  const highs = CURVE_PHASES.map((p) => expected[p].high);
  const high = Math.min(100, Math.ceil((Math.max(...values, ...highs) + 5) / 10) * 10);
  const low = Math.max(0, Math.min(high - 40, Math.floor((Math.min(...values, ...lows) - 5) / 10) * 10));
  const y = (v: number) => top + ((high - v) / (high - low)) * (H - top - bottom);
  const x = (i: number) => left + (i * (W - left - right)) / (CURVE_PHASES.length - 1);
  const points = values.map((v, i) => `${x(i)},${y(v)}`).join(" ");
  const band = [...highs.map((v, i) => `${x(i)},${y(v)}`), ...lows.map((v, i) => `${x(i)},${y(v)}`).reverse()].join(" ");
  const label = t("Évolution de T0 à T3 : {values} ; DTO attendue : {expected}", {
    values: CURVE_PHASES.map((p) => `${p} ${percentText(family.dto[p])}`).join(", "),
    expected: CURVE_PHASES.map((p) => `${p} ${bandText(expected[p])}`).join(", "),
  });
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label} className="block h-auto w-full overflow-visible text-meta">
        {/* DTO attendue : bande de la trajectoire, nommée par la légende. */}
        <polygon points={band} fill="currentColor" fillOpacity={0.16} stroke="none" className="text-ink-muted" />
        {/* Courbe */}
        <polyline points={points} fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinejoin="round" className="text-ink-soft" />
        {CURVE_PHASES.map((p, i) => {
          const v = family.dto[p];
          const tone = TONE_TEXT[colorTone(dtoColor(v, expected[p].low))];
          const current = p === phase;
          return (
            <g key={p}>
              <circle cx={x(i)} cy={y(v)} r={current ? 4.5 : 3.25} fill="currentColor" className={tone} stroke="var(--color-surface)" strokeWidth={1.5} />
              <text x={x(i)} y={y(v) - 8} textAnchor="middle" className={clsx("fill-ink text-caps tabular-nums", current && "font-semibold")}>
                {percentText(v)}
              </text>
              <text x={x(i)} y={H - 5} textAnchor="middle" className={clsx("text-caps", current ? "fill-ink font-semibold" : "fill-ink-muted")}>
                {p}
              </text>
            </g>
          );
        })}
      </svg>
      <p className="mt-1 flex items-center gap-1.5 text-meta leading-snug text-ink-muted">
        <span aria-hidden className="inline-block h-2.5 w-4 shrink-0 rounded-sm bg-ink-muted/30" />
        {t("DTO attendue")}
      </p>
    </div>
  );
}

/** Pannes, dommages et réparations de chaque temps, baisses des incidents : une ligne par temps qui a changé quelque
 *  chose. */
export function LegLines({ family, hours }: { family: MaintenanceFamily; hours: LogisticsMaintenance["hours"] }) {
  const lines = (["T1", "T2", "T3"] as const).flatMap((p) => {
    const failures = family.failures[p] ?? 0;
    const repairs = family.repairs[p] ?? 0;
    return failures > 0 || repairs > 0
      ? [
          t("{phase} ({hours} h) : pannes et dommages −{failures}, réparations +{repairs} (points de DTO).", {
            phase: p,
            hours: formatNumber(Math.round(hours[p] ?? 0)),
            failures: num(failures),
            repairs: num(repairs),
          }),
        ]
      : [];
  });
  const drops = CURVE_PHASES.flatMap((p) => {
    const n = family.drops[p];
    return n ? [t("Incidents de la DIREX à {phase} : −{n} (points de DTO).", { n: num(n), phase: p })] : [];
  });
  const all = [...lines, ...drops];
  if (!all.length) return <p className="text-meta leading-snug text-ink-muted">{t("Aucune panne de T0 à T3.")}</p>;
  return (
    <ul className="space-y-0.5 text-meta leading-snug text-ink-soft">
      {all.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}

/** Famille montrée par la courbe : la famille désignée, sinon la plus en retard au temps affiché, sinon la première. */
export function initialFamily(m: LogisticsMaintenance, focus: MaterielFamily | null): MaterielFamily | null {
  if (focus && m.families.some((f) => f.family === focus)) return focus;
  return m.lowest?.family ?? m.families[0]?.family ?? null;
}
